/**
 * @name TotemContext
 * @description Contexto específico para o totem - gerencia a organização atual baseado no operador logado
 * @author Keven Adan Bezerra | kevenadanb
 * @package src/contexts/TotemContext/TotemContext.tsx
 */

import React, { createContext, useContext, useEffect, useState } from "react";
import { useOperadorContext } from "../OperadorContext";

// Interface para o contexto do totem
export interface TotemContextType {
  organizationId: number | null;
  setOrganizationId: (id: number) => void;
  isReady: boolean; // Indica se o contexto está pronto para uso
}

const TotemContext = createContext<TotemContextType | undefined>(undefined);

export const TotemProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { operadorAtual } = useOperadorContext();

  // Organização baseada no operador logado
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);

  // Atualizar organização quando operador logar
  useEffect(() => {
    if (operadorAtual?.organizacao_id) {
      console.log(
        "🏢 Definindo organização do totem:",
        operadorAtual.organizacao_id,
      );
      setOrganizationId(operadorAtual.organizacao_id);
      setIsReady(true);
    } else if (operadorAtual) {
      // Operador logado mas sem organização
      alert(
        "Operador não possui organização vinculada. Contate o administrador.",
      );
      setOrganizationId(null);
      setIsReady(false);
    } else {
      console.log("⚠️ Sem operador logado - aguardando...");
      setOrganizationId(null);
      setIsReady(false);
    }
  }, [operadorAtual]);

  return (
    <TotemContext.Provider
      value={{
        organizationId,
        setOrganizationId,
        isReady,
      }}
    >
      {children}
    </TotemContext.Provider>
  );
};

export const useTotemContext = () => {
  const context = useContext(TotemContext);
  if (!context) {
    throw new Error("useTotemContext must be used within a TotemProvider");
  }
  return context;
};

export default TotemContext;
